import React, { PropsWithChildren } from "react"
import { Form, Formik, FormikHelpers, useFormikContext } from "formik"
import { makeStyles } from "tss-react/mui"
import * as yup from "yup"
import { renderTimeViewClock } from "@mui/x-date-pickers"
import { Box, DialogContent, Grid, InputAdornment, MenuItem, TextField, Typography } from "@mui/material"
import { LocalizedEnum, LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { useAppLocalization } from "../../../../../localization/components/AppLocalizationProvider"
import { toCurrency, toDineroLossy, toMonetaryAmount } from "../../../../../currency/currencyLib"
import { StandardDialog } from "../../../../../../shared/components/dialogs/StandardDialog"
import { StandardButton } from "../../../../../../shared/components/buttons/StandardButton"
import {
    FormikDateTimePicker,
    FormikDropdown,
    FormikNumberField,
} from "../../../../../../shared/components/forms/Formik"
import { useDateTime } from "../../../../../localization/useDateTime"
import { useMoney } from "../../../../../currency/useMoney"
import { CurrencyCode } from "../../../priceList/priceListTypes"
import { iterateStringEnum } from "../../../../../../lib/enums"
import { NumberFormatCustom } from "../FormikCurrencyField"
import { MarketingTags } from "./MarketingTags"
import {
    CreateUpdateProductFormValues,
    DiscountType,
    OfferDetailsFormValues,
    OfferDetailsType,
} from "../../productsTypes"

const useStyles = makeStyles()((theme) => ({
    title: {
        color: theme.palette.tailwind.slate[600],
    },
    dialogPaper: {
        padding: "1rem",
        justifyContent: "center",
        maxWidth: "73rem",
    },
    dialogTitle: {
        textAlign: "center",
        fontSize: "2.5rem",
        fontWeight: 600,
        paddingTop: "2rem",
        color: theme.palette.tailwind.slate[700],
    },
    content: {
        width: "100%",
        padding: "0 4rem",
    },
    box: {
        width: "100%",
        boxShadow: "0px 2px 12px 0px #0000001F",
        borderRadius: "0.5rem",
    },
    boxUpdate: {
        gap: "1rem",
        padding: "2rem 1rem",
    },
    boxTitle: {
        padding: "1.25rem",
        color: theme.palette.tailwind.slate[600],
        borderBottom: `1px solid ${theme.palette.tailwind.slate[200]}`,
    },
    inputUi: {
        width: "100%",
        maxWidth: "12.5rem",
    },
    inputUiWide: {
        width: "100%",
        maxWidth: "16rem",
    },
    saveButton: {
        paddingLeft: "2rem",
        paddingRight: "2rem",
    },
}))

type OfferDialogProps = Readonly<
    PropsWithChildren<{
        open: boolean
        onClose: () => void
        initialValues?: OfferDetailsFormValues
        handleSaveOffer: (data: OfferDetailsType) => void
    }>
>

function mapAvailability(
    dateTimeFactory: ReturnType<typeof useDateTime>,
    availabilityDetails: OfferDetailsFormValues["availabilityDetails"],
) {
    const from = availabilityDetails?.dateTimeInterval?.from
        ? dateTimeFactory.toApi(availabilityDetails.dateTimeInterval.from)
        : undefined
    const to = availabilityDetails?.dateTimeInterval?.to
        ? dateTimeFactory.toApi(availabilityDetails.dateTimeInterval.to)
        : undefined

    return {
        dateTimeInterval: {
            ...(from && { from }),
            ...(to && { to }),
        },
    }
}

function scalePercentage(amount: string | number | undefined) {
    return {
        amount: Number(amount) * 10,
        scale: 1,
    }
}

export function OfferDialog({ open, onClose, initialValues, handleSaveOffer }: OfferDialogProps) {
    const { values } = useFormikContext<CreateUpdateProductFormValues>()
    const { l10n } = useAppLocalization()
    const { classes } = useStyles()
    const dateTimeFactory = useDateTime()
    const moneyFactory = useMoney({})

    const defaultInitialValues = {
        discount: {
            type: DiscountType.NONE,
        },
        availabilityDetails: {},
    }

    const validationSchema = yup.object({
        discount: yup.object({
            type: yup.string().required("Type is required"),
            percentage: yup
                .object()
                .when("type", {
                    is: (type: DiscountType) => type === DiscountType.DISCOUNT_PERCENTAGE,
                    then: () =>
                        yup.object({
                            amount: yup.string().required(l10n.getString("required-field")).min(1),
                        }),
                })
                .when("type", {
                    is: (type: DiscountType) => type !== DiscountType.DISCOUNT_PERCENTAGE,
                    then: () =>
                        yup.object({
                            amount: yup.string().notRequired(),
                        }),
                }),
            discountedPrice: yup.number().when("type", {
                is: (type: DiscountType) => type === DiscountType.DISCOUNT_PRICE,
                then: (schema) => schema.required(l10n.getString("required-field")),
                otherwise: (schema) => schema.notRequired(),
            }),
        }),
        productsOnSale: yup.number(),
        availabilityDetails: yup.object({
            dateTimeInterval: yup.object({
                from: yup.date(),
                to: yup.date(),
            }),
        }),
    })

    function handleSubmit(form: OfferDetailsFormValues, _helpers: FormikHelpers<OfferDetailsFormValues>) {
        const { discount, availabilityDetails, productsOnSale } = form

        const availability = mapAvailability(dateTimeFactory, availabilityDetails)

        const baseDiscount = {
            type: discount.type,
            ...(discount.type !== DiscountType.NONE && {
                displayLabelType: discount.displayLabelType,
            }),
        }

        let discountPayload: OfferDetailsType["discount"] = baseDiscount

        if (discount.type === DiscountType.DISCOUNT_PERCENTAGE) {
            discountPayload = {
                ...baseDiscount,
                percentage: scalePercentage(discount.percentage?.amount),
            }
        }

        if (discount.type === DiscountType.DISCOUNT_PRICE) {
            discountPayload = {
                ...baseDiscount,
                discountedPrice: toMonetaryAmount(
                    toDineroLossy(discount.discountedPrice!, toCurrency(values.currency)),
                ),
            }
        }

        handleSaveOffer({
            ...(productsOnSale && { productsOnSale }),
            availabilityDetails: availability,
            discount: discountPayload,
        })

        onClose()
    }

    return (
        <StandardDialog
            open={open}
            onClose={onClose}
            dialogTitle={
                initialValues ? (
                    <LocalizedStrict id="product-create-offer-dialog-edit-title">
                        <Typography className={classes.dialogTitle}>Edit offer</Typography>
                    </LocalizedStrict>
                ) : (
                    <LocalizedStrict id="product-create-offer-dialog-create-title">
                        <Typography className={classes.dialogTitle}>Create offer</Typography>
                    </LocalizedStrict>
                )
            }
            slotProps={{ paper: { className: classes.dialogPaper } }}
            scroll="body"
        >
            <Grid container justifyContent="center" alignItems="center" direction="column" wrap="nowrap">
                <Grid container flexDirection="column" gap={5} className={classes.content}>
                    <Grid
                        container
                        alignItems="flex-end"
                        justifyContent="space-between"
                        flexWrap="nowrap"
                        gap={3}
                        padding="0 2.5rem"
                    >
                        <Typography variant="h4" className={classes.title}>
                            {values.name ?? "-"}
                        </Typography>
                        <Grid container width="auto" flexDirection="column" alignItems="flex-end">
                            <LocalizedStrict id="product-create-offer-dialog-normal-price">
                                <Typography>Original price:</Typography>
                            </LocalizedStrict>
                            <Typography variant="h4">
                                {values.price != null
                                    ? moneyFactory.format(
                                          toDineroLossy(values.price, toCurrency(values.currency ?? CurrencyCode.DKK)),
                                      )
                                    : ""}
                            </Typography>
                        </Grid>
                    </Grid>

                    <Formik<OfferDetailsFormValues>
                        enableReinitialize
                        initialValues={initialValues || defaultInitialValues}
                        validationSchema={validationSchema}
                        onSubmit={handleSubmit}
                        validateOnBlur={false}
                        validateOnChange={false}
                    >
                        <Form>
                            <OfferDetailsFormContent
                                maxPrice={values?.price}
                                currency={values.currency}
                                itemsForSalePerDay={values.itemsForSalePerDay}
                            />
                        </Form>
                    </Formik>
                </Grid>
            </Grid>
        </StandardDialog>
    )
}

type OfferDetailsFormContentProps = Readonly<{
    itemsForSalePerDay?: number
    maxPrice?: number
    currency?: string
}>

export function OfferDetailsFormContent({ itemsForSalePerDay, maxPrice, currency }: OfferDetailsFormContentProps) {
    const { classes, cx } = useStyles()
    const { values, setFieldValue, errors } = useFormikContext<OfferDetailsFormValues>()
    const parentValues = { stock: { itemsForSalePerDay } }
    const { discount, availabilityDetails } = values

    return (
        <DialogContent>
            <Grid
                container
                flexWrap="nowrap"
                justifyContent="space-between"
                alignItems="flex-start"
                marginBottom="2.5rem"
            >
                <Grid
                    container
                    xs={7}
                    flexDirection="column"
                    alignItems="flex-start"
                    className={cx(classes.box, classes.boxUpdate)}
                >
                    <Grid maxWidth="20rem" width="100%">
                        <LocalizedStrict
                            id="product-create-offer-dialog-price-adjustment-field"
                            attrs={{ label: true }}
                        >
                            <FormikDropdown name="discount.type" label="Price adjustment" fullWidth>
                                {[...iterateStringEnum(DiscountType)].map((type) => (
                                    <MenuItem key={type} value={type}>
                                        <LocalizedEnum
                                            base="product-create-offer-dialog-price-adjustment-option"
                                            enumValue={type}
                                        >
                                            <span>{type}</span>
                                        </LocalizedEnum>
                                    </MenuItem>
                                ))}
                            </FormikDropdown>
                        </LocalizedStrict>
                    </Grid>
                    {discount.type === DiscountType.DISCOUNT_PERCENTAGE && (
                        <LocalizedStrict id="product-create-offer-dialog-percentage-field" attrs={{ label: true }}>
                            <FormikNumberField
                                label="Percentage"
                                name="discount.percentage.amount"
                                integerOnly
                                maxLength={3}
                                min={0}
                                max={100}
                                InputProps={{
                                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                                }}
                                inputProps={{ style: { textAlign: "right" } }}
                                error={Boolean(errors.discount?.percentage)}
                                className={classes.inputUi}
                            />
                        </LocalizedStrict>
                    )}
                    {discount.type === DiscountType.DISCOUNT_PRICE && (
                        <LocalizedStrict id="product-create-offer-dialog-adjust-price-field" attrs={{ label: true }}>
                            <TextField
                                label="New price"
                                value={discount.discountedPrice}
                                onChange={(e) => {
                                    setFieldValue("discount.discountedPrice", e.target.value)
                                }}
                                InputProps={{
                                    inputComponent: NumberFormatCustom as any,
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            {currency ?? CurrencyCode.DKK}
                                        </InputAdornment>
                                    ),
                                }}
                                inputProps={{
                                    isAllowed: (values: { floatValue?: number }) => {
                                        if (values.floatValue == null) return true
                                        return maxPrice == null || values.floatValue <= maxPrice
                                    },
                                }}
                                error={Boolean(errors.discount?.discountedPrice)}
                                helperText={errors.discount?.discountedPrice}
                                className={classes.inputUi}
                            />
                        </LocalizedStrict>
                    )}

                    <Box display="flex" alignItems="center" gap="0.5rem">
                        <LocalizedStrict id="product-create-offer-dialog-items-field">
                            <Typography variant="body1" fontWeight={500}>
                                Available items per day:
                            </Typography>
                        </LocalizedStrict>
                        <Typography variant="body1" fontWeight={400}>
                            {parentValues.stock?.itemsForSalePerDay ?? "–"}
                        </Typography>
                    </Box>

                    {discount.type !== DiscountType.NONE && <MarketingTags />}
                </Grid>
                <Grid item xs={4} className={classes.box}>
                    <LocalizedStrict id="product-create-offer-dialog-available-offer-period">
                        <Typography variant="subtitle1" className={classes.boxTitle}>
                            Offer period
                        </Typography>
                    </LocalizedStrict>
                    <Grid container flexDirection="column" gap={2} padding="2rem 1.25rem">
                        <LocalizedStrict
                            id="product-create-offer-dialog-available-from-date-picker"
                            attrs={{ label: true }}
                        >
                            <FormikDateTimePicker
                                name="availabilityDetails.dateTimeInterval.from"
                                range="all"
                                label="Available from"
                                format={"MM/dd/yyyy\u00A0hh:mm\u00A0a"}
                                maxDateTime={availabilityDetails.dateTimeInterval?.to}
                                slotProps={{ field: { clearable: true } }}
                            />
                        </LocalizedStrict>
                        <LocalizedStrict
                            id="product-create-offer-dialog-available-to-date-picker"
                            attrs={{ label: true }}
                        >
                            <FormikDateTimePicker
                                name="availabilityDetails.dateTimeInterval.to"
                                range="all"
                                label="Available to"
                                format={"MM/dd/yyyy\u00A0hh:mm\u00A0a"}
                                minDateTime={availabilityDetails.dateTimeInterval?.from}
                                slotProps={{ field: { clearable: true } }}
                            />
                        </LocalizedStrict>
                    </Grid>
                </Grid>
            </Grid>
            <Grid container justifyContent="center">
                <LocalizedStrict id="product-create-offer-dialog-save-button">
                    <StandardButton
                        fullWidth={false}
                        type="submit"
                        size="medium"
                        updateDefaultClasses={classes.saveButton}
                    >
                        Save
                    </StandardButton>
                </LocalizedStrict>
            </Grid>
        </DialogContent>
    )
}

